import request from 'yeself-common/utils/RequestUtils';
import { ApplicationId } from 'yeself-common/typings/application/Application';
import { GetBatchedGuideListForApplicationResponse, GuideGraphsMap, GuidesListForApplication } from 'yeself-common/typings/guide/GuideResponse';

import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

// @ts-expect-error: Cannot find module './queries/GetAttributeUsageByApplicationIdQuery.graphql' or its corresponding type declarations
import GetAttributeUsageByApplicationIdQuery from './queries/GetAttributeUsageByApplicationIdQuery.graphql';

export const fetchApplicationGuides = (appId) =>
    request.get(`${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides`);

export const fetchGuideGraphsByIds = async (
    applicationId: ApplicationId,
    ids: string[]
): Promise<GuideGraphsMap> => {
    try {
        const response = await request.get(
            `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/guideGraphsByIds`,
            {
                query: { ids, excludes: ['location'] }
            }
        );

        return response.body;
    } catch (error) {
        console.error('Error fetching guide graphs:', error);
        throw new Error('Failed to fetch guide graphs');
    }
};

export const fetchBatchedApplicationGuides = async (
    applicationId: ApplicationId
): Promise<GuidesListForApplication> => {
    try {
        const result: { body: GetBatchedGuideListForApplicationResponse } = await request.get(
            `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/guides?view=batchedEditorAvailableGuides`
        );

        const { guides } = result.body.data;
        const { guideBatches } = result.body;

        const allPromises = guideBatches.map(batch =>
            fetchGuideGraphsByIds(applicationId, batch).catch(error => {
                console.error(`Failed to fetch guide graphs for batch: ${batch}`, error);
                return error;
            })
        );

        const fetchedGuideGraphArrays = await Promise.all(allPromises);

        const fetchedGuideGraphs = fetchedGuideGraphArrays.reduce(
            (acc: GuideGraphsMap, guideGraphMap: GuideGraphsMap): GuideGraphsMap => {
                Object.assign(acc, guideGraphMap);
                return acc;
            }, {}
        );

        const enrichedGuides = guides.map(guide => ({
            ...guide,
            graph: fetchedGuideGraphs[guide.id]
        }));

        return ({
            application: result.body.data.application,
            evaluations: result.body.data.evaluations,
            guides: enrichedGuides
        });
    } catch (error) {
        console.error('Failed to fetch batched application guides:', error);
        throw new Error('Error fetching batched application guides');
    }
}

export const removeApplicationGuide = (appId, guideId) =>
    request.del(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}`
    );

export const duplicateGuide = (appId, guideId, data) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}/duplicate`,
        data
    );

export const updateGuide = (appId, guideId, data) =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}`,
        data
    );

export const updateTour = (appId, tourId, data) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${tourId}/update`,
        data
    );

export const updateGuideHost = (appId, guideId, data) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}/replace`,
        data
    );

export const createNewGuideRequest = (appId, data) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides`,
        data
    );

export const fetchGuideRulesService = (appId, guideId) =>
    request.get(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}/graph`
    );

export const updateGuideRulesService = (appId, guideId, data) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/guides/${guideId}/update`,
        data
    );

export const fetchAttributeForRuleService = (applicationId) =>
    request.post(
        EnvironmentConfiguration.HASURA_URL,
        {
            query: GetAttributeUsageByApplicationIdQuery,
            variables: { applicationId },
        },
        {
            header: {
                'Content-Type': 'application/json',
            },
        }
    );

export const fetchContextOptionsForRuleService = (companyId) =>
    request.get(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/company/${companyId}/context`,
        {
            header: {
                'Content-Type': 'application/json',
            },
        }
    );
